/**
 * Timezone helper for aligning schedules between two parties (any timezones).
 * Stored times are treated as being in the "client" (primary) timezone.
 */

const STORAGE_KEYS = {
  CLIENT_TZ: 'scheduling_client_timezone',
  ASSISTANT_TZ: 'scheduling_assistant_timezone',
  CLIENT_LABEL: 'scheduling_client_label',
  ASSISTANT_LABEL: 'scheduling_assistant_label',
};

function getBrowserTimezone() {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const DEFAULT_CLIENT_LABEL = 'Zona 1';
const DEFAULT_ASSISTANT_LABEL = 'Zona 2';

export function getClientTimezone() {
  return typeof localStorage !== 'undefined'
    ? (localStorage.getItem(STORAGE_KEYS.CLIENT_TZ) || getBrowserTimezone())
    : getBrowserTimezone();
}

export function getAssistantTimezone() {
  return typeof localStorage !== 'undefined'
    ? (localStorage.getItem(STORAGE_KEYS.ASSISTANT_TZ) || 'UTC')
    : 'UTC';
}

export function getClientLabel() {
  return typeof localStorage !== 'undefined'
    ? (localStorage.getItem(STORAGE_KEYS.CLIENT_LABEL) || DEFAULT_CLIENT_LABEL)
    : DEFAULT_CLIENT_LABEL;
}

export function getAssistantLabel() {
  return typeof localStorage !== 'undefined'
    ? (localStorage.getItem(STORAGE_KEYS.ASSISTANT_LABEL) || DEFAULT_ASSISTANT_LABEL)
    : DEFAULT_ASSISTANT_LABEL;
}

export function setClientTimezone(tz) {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEYS.CLIENT_TZ, tz);
}

export function setAssistantTimezone(tz) {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEYS.ASSISTANT_TZ, tz);
}

export function setClientLabel(label) {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEYS.CLIENT_LABEL, label);
}

export function setAssistantLabel(label) {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEYS.ASSISTANT_LABEL, label);
}

/**
 * Get how many minutes the given timezone is AHEAD of UTC at the given date (e.g. UTC+5 => 300).
 */
function getOffsetMinutesAheadOfUTC(tz, date) {
  const utcMidnight = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false });
  const str = formatter.format(utcMidnight);
  const [h, m] = str.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Parse a date string (YYYY-MM-DD) and time string (HH:mm or HH:mm:ss) in the primary (client) timezone,
 * then return formatted times in both timezones.
 * @param {string} dateStr - e.g. "2025-03-09"
 * @param {string} timeStr - e.g. "09:00" or "09:00:00"
 * @returns {{ client: string, assistant: string, clientFull?: string, assistantFull?: string } | null}
 */
const SHORT_WEEKDAY_TO_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/**
 * Calendar year / month / day for an instant in an IANA timezone (for agenda cells).
 * @param {Date|string|number} dateInput
 * @param {string} timeZone
 * @returns {{ y: number, m: number, d: number } | null}  m = 1..12
 */
export function getYmdInTimeZone(dateInput, timeZone) {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const s = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
    const [y, m, day] = s.split('-').map(Number);
    return { y, m, d: day };
  } catch {
    return null;
  }
}

/**
 * Weekday 0 = Sunday … 6 = Saturday for a calendar date in the given timezone.
 * @param {number} year
 * @param {number} monthIndex 0..11
 * @param {number} day 1..31
 */
export function getWeekdayInTimeZone(year, monthIndex, day, timeZone) {
  const d = new Date(Date.UTC(year, monthIndex, day, 12, 0, 0, 0));
  try {
    const short = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(d);
    const idx = SHORT_WEEKDAY_TO_INDEX[short];
    return idx === undefined ? new Date(year, monthIndex, day).getDay() : idx;
  } catch {
    return new Date(year, monthIndex, day).getDay();
  }
}

export function formatInBothZones(dateStr, timeStr) {
  if (!dateStr) return null;
  const clientTz = getClientTimezone();
  const assistantTz = getAssistantTimezone();
  try {
    const [hours = 0, minutes = 0] = (timeStr || '00:00').toString().split(':').map(Number);
    const dateOnly = dateStr.toString().trim().split('T')[0];
    const [y, m, d] = dateOnly.split('-').map(Number);
    if (!y || !m || !d) return null;
    const refDate = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
    const clientOffsetMin = getOffsetMinutesAheadOfUTC(clientTz, refDate);
    // "09:00" in client TZ = this UTC moment:
    const utcMs = Date.UTC(y, m - 1, d, hours, minutes, 0, 0) - clientOffsetMin * 60 * 1000;
    const utcDate = new Date(utcMs);
    const fmt = { hour: '2-digit', minute: '2-digit', hour12: false };
    const clientFormatted = new Intl.DateTimeFormat('es-ES', { ...fmt, timeZone: clientTz }).format(utcDate);
    const assistantFormatted = new Intl.DateTimeFormat('es-ES', { ...fmt, timeZone: assistantTz }).format(utcDate);
    const fullFmt = { dateStyle: 'short', timeStyle: 'short', hour12: false };
    return {
      client: clientFormatted,
      assistant: assistantFormatted,
      clientFull: new Intl.DateTimeFormat('es-ES', { ...fullFmt, timeZone: clientTz }).format(utcDate),
      assistantFull: new Intl.DateTimeFormat('es-ES', { ...fullFmt, timeZone: assistantTz }).format(utcDate),
    };
  } catch (e) {
    return null;
  }
}

/**
 * Get current time formatted in both timezones (for live clock).
 */
export function getCurrentTimeInBothZones() {
  const clientTz = getClientTimezone();
  const assistantTz = getAssistantTimezone();
  const now = new Date();
  const fmt = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  return {
    client: new Intl.DateTimeFormat('es-ES', { ...fmt, timeZone: clientTz }).format(now),
    assistant: new Intl.DateTimeFormat('es-ES', { ...fmt, timeZone: assistantTz }).format(now),
  };
}

/**
 * Format UTC offset for a timezone at the given date (e.g. "+05:00", "-03:30").
 */
function getOffsetString(tz, date = new Date()) {
  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(date);
    const part = parts.find((p) => p.type === 'timeZoneName');
    if (part && part.value) return part.value;
    const offsetMin = getOffsetMinutesAheadOfUTC(tz, date);
    const h = Math.floor(Math.abs(offsetMin) / 60);
    const m = Math.abs(offsetMin) % 60;
    const sign = offsetMin >= 0 ? '+' : '-';
    return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

let _cachedZoneList = null;

/**
 * All supported IANA timezones with labels (e.g. "Europe/Madrid (UTC+1)").
 * Cached after first call. Sorted by offset then by name.
 */
export function getTimeZoneOptions() {
  if (_cachedZoneList) return _cachedZoneList;
  const now = new Date();
  let ids = [];
  if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
    try {
      ids = Intl.supportedValuesOf('timeZone');
    } catch (_) {}
  }
  if (ids.length === 0) {
    ids = [
      'UTC', 'Europe/Madrid', 'Europe/London', 'America/New_York', 'America/Los_Angeles',
      'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Bangkok', 'Asia/Karachi',
      'Australia/Sydney', 'America/Sao_Paulo',
    ];
  }
  const withOffset = ids.map((value) => {
    const offset = getOffsetString(value, now);
    const label = offset ? `${value} (${offset})` : value;
    return { value, label };
  });
  withOffset.sort((a, b) => {
    const oA = getOffsetMinutesAheadOfUTC(a.value, now);
    const oB = getOffsetMinutesAheadOfUTC(b.value, now);
    if (oA !== oB) return oA - oB;
    return (a.value || '').localeCompare(b.value || '');
  });
  _cachedZoneList = withOffset;
  return withOffset;
}
